import React from 'react';
import { EmptyState, MaterialIcon } from '../../ui';
import { SESSION_CATEGORIES, getSessions } from '../../../services/activityService';
import SessionRow from './SessionRow';
import { formatExact, formatRole } from './activityFormat';

const PAGE_SIZE = 25;

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

/**
 * Sessions view: server-side sessions paged with a cursor. Filters (category,
 * search, global toggles) re-query the server; nothing is reconstructed from a
 * raw-row window in the browser.
 */
const SessionFeed = ({ hoaId, includeAdmin, includeArchived, refreshKey }) => {
  const [category, setCategory] = React.useState('all');
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [sessions, setSessions] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    if (!hoaId) return undefined;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const page = await getSessions({ hoaId, limit: PAGE_SIZE, category, includeAdmin, includeArchived, search });
        if (requestIdRef.current !== requestId) return;
        setSessions(page.sessions);
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setSessions([]);
        setNextCursor(null);
        setError(err?.message || 'Activity could not be loaded.');
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    })();
    return undefined;
  }, [hoaId, category, includeAdmin, includeArchived, search, refreshKey]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const page = await getSessions({
        hoaId, before: nextCursor, limit: PAGE_SIZE, category, includeAdmin, includeArchived, search,
      });
      if (requestIdRef.current !== requestId) return;
      setSessions((current) => {
        const seen = new Set(current.map((s) => s.session_key));
        return [...current, ...page.sessions.filter((s) => !seen.has(s.session_key))];
      });
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err?.message || 'More activity could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleExport = () => {
    const header = ['Started', 'Ended', 'Person', 'Role', 'Account', 'Events', 'Documents opened', 'Sections', 'Admin view-as'];
    const lines = sessions.map((s) => [
      s.started_at, s.ended_at, s.full_name, formatRole(s.role_name), s.account_state, s.event_count,
      s.doc_views, (s.sections || []).join(' '), s.admin_view_as ? 'yes' : 'no',
    ].map(csvCell).join(','));
    const blob = new Blob([[header.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'activity_sessions.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter sessions">
          {SESSION_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              aria-pressed={category === c.key}
              className={`px-3.5 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                category === c.key
                  ? 'bg-secondary/10 border-secondary/40 text-secondary'
                  : 'bg-white border-amber-100/60 text-slate-600 hover:border-secondary/40'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex-1 min-w-0 sm:flex-none">
            <span className="sr-only">Search people</span>
            <MaterialIcon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search people…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-white border border-amber-100/60 rounded-full w-full sm:w-56 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all"
            />
          </label>
          <button
            type="button"
            onClick={handleExport}
            disabled={sessions.length === 0}
            className="px-4 py-2 text-xs border border-secondary text-secondary hover:bg-secondary hover:text-white font-bold rounded-full inline-flex items-center gap-1.5 transition-all disabled:opacity-50"
          >
            <MaterialIcon name="download" size={16} /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-slate-500">Loading activity…</p>
      ) : error && sessions.length === 0 ? (
        <p className="py-12 text-center text-sm text-red-700">{error}</p>
      ) : sessions.length === 0 ? (
        <EmptyState
          icon="history"
          title="No sessions match"
          body="Try another filter, or turn on “Show admin & test” or “Include archived”."
        />
      ) : (
        <>
          <ul className="space-y-3">
            {sessions.map((s) => <SessionRow key={s.session_key} session={s} hoaId={hoaId} />)}
          </ul>
          {error && <p className="text-center text-sm text-red-700">{error}</p>}
          <div className="flex flex-col items-center gap-2 pt-2">
            {nextCursor ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="border border-secondary text-secondary px-8 py-3 rounded-full font-bold text-sm hover:bg-secondary hover:text-white transition-all disabled:opacity-60"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : (
              <span className="text-xs text-slate-500">
                End of activity · oldest shown {formatExact(sessions[sessions.length - 1]?.started_at)}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SessionFeed;
